/**
 * 통합검색 API 서비스
 * POST /WNsearch.do API를 호출하여 검색 결과를 처리하는 서비스 모듈
 */

// API 엔드포인트
const API_ENDPOINT = '/WNsearch.do';

// 기본 검색 파라미터
const DEFAULT_SEARCH_PARAMS = {
  sort: 'RANK',           // 정렬: RANK(정확도순), DATE(최신순)
  searchField: 'ALL',     // 검색 필드: ALL, TITLE, CONTENT
  startCount: 0,          // 시작 번호
  startDate: '',          // 시작 날짜
  endDate: '',            // 종료 날짜
  searchSel: '3'          // 페이지당 결과 수
};

/**
 * 검색 API 서비스 클래스
 */
class SearchApiService {
  
  /**
   * 검색 API 호출
   * @param {Object} params - 검색 파라미터
   * @param {string} params.query - 검색어
   * @param {string} params.realQuery - 실제 검색어 (선택사항, query와 동일)
   * @param {string} params.orgType - 기관 유형 (job-info, foundation, senior-info, center)
   * @param {string} params.menuType - 메뉴 유형 (all, senior-center, aljari 등)
   * @param {string} params.centerType - 센터 유형 (dongjak, yeongdeungpo 등)
   * @param {Object} params.filters - 추가 필터 (sort, searchField 등)
   * @returns {Promise<Object>} 검색 결과
   */
  static async search(params) {
    try {
      // API 요청 파라미터 구성
      const searchParams = SearchApiService.buildSearchParams(params);
      
      // API 호출
      const response = await SearchApiService.callSearchApi(searchParams);
      
      // 결과 처리 및 반환
      return SearchApiService.processSearchResults(response);
      
    } catch (error) {
      throw SearchApiService.handleApiError(error);
    }
  }

  /**
   * 검색 파라미터 구성
   * @param {Object} params - 입력 파라미터
   * @returns {Object} API 요청 파라미터
   */
  static buildSearchParams(params) {
    
    const {
      query,
      realQuery = query,
      orgType = 'job-info',
      menuType = 'ALL', 
      centerType = 'dongjak',
      filters = {}
    } = params;

    // Collection 결정
    let collection;
    
    if (orgType === 'center' && centerType) {
      // 센터의 경우: API 가이드에 따라 menuType을 직접 사용 (cen_news, cen_edu 등)
      // 단, 'ALL'인 경우만 'ALL'로 유지
      collection = (menuType === 'ALL') ? 'ALL' : menuType;
    } else if (menuType === 'ALL') {
      // 전체 탭의 경우 항상 ALL
      collection = 'ALL';
    } else {
      // 개별 메뉴의 경우 menuType이 이미 collection 코드
      collection = menuType;
    }

    // orgCd 결정 (센터인 경우만)
    // centerType이 이미 centerOrgs의 code 값 (djc, ydp 등)이므로 직접 사용
    const orgCd = orgType === 'center' ? centerType : null;

    // searchPage 결정 (API 가이드 기준)
    const searchPageMap = {
      'job-info': 'job',        // 일자리정보몽땅
      'foundation': 'fou',      // 재단
      'senior-info': 'por',     // 라이프몽땅
      'center': 'cen'           // 센터
    };
    const searchPage = searchPageMap[orgType] || 'job';

    // period 파라미터를 startDate/endDate로 변환
    let startDate = filters.startDate || '';
    let endDate = filters.endDate || '';
    
    if (filters.period && !filters.startDate && !filters.endDate) {
      const dates = SearchApiService.convertPeriodToDates(filters.period);
      startDate = dates.startDate;
      endDate = dates.endDate;
    }

    // field 파라미터를 searchField로 변환
    let searchField = filters.searchField || DEFAULT_SEARCH_PARAMS.searchField;
    if (filters.field) {
      searchField = filters.field;
    }

    // 최종 파라미터 구성 (API 가이드 기준 파라미터만 포함)
    const finalParams = {
      query: query.trim(),
      realQuery: realQuery.trim(),
      collection,
      searchPage,
      startDate,
      endDate,
      searchField,
      sort: filters.sort || DEFAULT_SEARCH_PARAMS.sort,
      startCount: filters.startCount || DEFAULT_SEARCH_PARAMS.startCount,
      searchSel: filters.searchSel || DEFAULT_SEARCH_PARAMS.searchSel,
      ...(orgCd && { orgCd })
    };
    
    return finalParams;
  }

  /**
   * 검색 API 호출
   * @param {Object} searchParams - API 요청 파라미터 
   * @returns {Promise<Object>} API 응답
   */
  static async callSearchApi(searchParams) {
    
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(searchParams),
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const jsonResponse = await response.json();
      return jsonResponse;
    } else {
      // HTML 응답인 경우 (에러 페이지 등)
      throw new Error('API 응답 형식이 올바르지 않습니다.');
    }
  }

  /**
   * 검색 결과 처리
   * @param {Object} response - API 응답
   * @returns {Object} 가공된 검색 결과
   */
  static processSearchResults(response) {
    
    try {
      // 실제 API 응답 구조에 따른 데이터 추출 (원본 구조 그대로 반환)
      const results = {
        totalCount: response.totalCount || 0,
        colResultList: response.colResultList || [],  // collections → colResultList로 수정
        colCountListAll: response.colCountListAll || {},
        colNameList: response.colNameList || [],
        pageLinks: response.pageLinks || '',
        realQuery: response.realQuery || '',
        collection: response.collection || ''
      };

      // 원본 데이터 보존 - 정규화 제거

      return results;
      
    } catch (error) {
      return {
        totalCount: 0,
        colResultList: []
      };
    }
  }

  /**
   * 날짜 형식 변환
   * @param {string} dateStr - 원본 날짜 문자열 (20250830121414 또는 2025.08.30 형식)
   * @returns {string} 변환된 날짜 문자열
   */
  static formatDate(dateStr) {
    if (!dateStr) return '';
    
    // 이미 포맷된 날짜인 경우 (2025.08.30)
    if (dateStr.includes('.')) {
      return dateStr;
    }
    
    // 숫자 형식인 경우 (20250830121414)
    if (dateStr.length >= 8) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${year}.${month}.${day}`;
    }
    
    return dateStr;
  }

  /**
   * 검색어 하이라이트 처리 (서버에서 이미 처리된 경우)
   * @param {string} text - 원본 텍스트
   * @returns {string} 하이라이트된 텍스트
   */
  static highlightKeywords(text) {
      alert(text);
    if (!text || typeof text !== 'string') return '';
    
    // 서버에서 이미 하이라이트 처리된 텍스트를 그대로 반환
    // <font color='red'>키워드</font> -> <span class="result-keyword">키워드</span>
    return text.replace(
      /<font color=['"]red['"]>(.*?)<\/font>/gi,
      '<span class="result-keyword">$1</span>'
    );
  }

  /**
   * API 오류 처리
   * @param {Error} error - 발생한 오류
   * @returns {Error} 처리된 오류
   */
  static handleApiError(error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return new Error('네트워크 연결을 확인해주세요.');
    } else if (error.message.includes('HTTP 4')) {
      return new Error('잘못된 요청입니다. 검색 조건을 확인해주세요.');
    } else if (error.message.includes('HTTP 5')) {
      return new Error('서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } else {
      return error;
    }
  }

  // getCollectionCode 메서드 제거됨 - 이제 각 컴포넌트에서 직접 처리

  /**
   * period 값을 startDate/endDate로 변환 (API 가이드 기준)
   * @param {string} period - 기간 선택 값 ('ALL', 'today', '1month', '1year')
   * @returns {Object} {startDate, endDate} - YYYY-MM-DD 형식
   */
  static convertPeriodToDates(period) {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0]; // YYYY-MM-DD 형식
    let startDate = '1970-01-01'; // API 가이드 기본값
    
    switch (period) {
      case 'today':
        startDate = endDate; // 오늘만
        break;
      case '1month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()).toISOString().split('T')[0];
        break;
      case '1year':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        break;
      case 'ALL':
      default:
        startDate = '1970-01-01'; // 전체 기간
        break;
    }
    
    return { startDate, endDate };
  }

  /**
   * 센터 조직 코드 조회 (더 이상 매핑 불필요 - centerType이 이미 조직 코드)
   * @param {string} centerType - 센터 코드 (djc, ydp 등)
   * @returns {string} 조직 코드
   */
  static getCenterOrgCode(centerType) {
    return centerType || 'djc';
  }
}

// 클래스 및 싱글톤 인스턴스 내보내기  
export { SearchApiService };
export const searchApiService = new SearchApiService();

// 추가 유틸리티 함수들
export const SearchUtils = {
  
  /**
   * URL 파라미터를 검색 파라미터로 변환
   * @param {URLSearchParams} urlParams - URL 파라미터
   * @returns {Object} 검색 파라미터
   */
  urlParamsToSearchParams(urlParams) {
    const searchPage = urlParams.get('searchPage');
    const orgCd = urlParams.get('orgCd');
    const query = urlParams.get('query');

    let orgType = 'job-info';
    let centerType = 'dongjak';

    // searchPage에서 orgType 결정
    switch (searchPage) {
      case 'por': orgType = 'job-info'; break;
      case 'fou': orgType = 'foundation'; break;
      case 'cam': orgType = 'senior-info'; break;
      case 'cen': orgType = 'center'; break;
      default: orgType = 'job-info';
    }

    // orgCd에서 centerType 결정
    if (orgType === 'center' && orgCd) {
      // orgCd가 이미 센터 코드이므로 직접 사용
      centerType = orgCd;
    }

    return {
      query: query || '',
      orgType,
      centerType,
      menuType: 'ALL'
    };
  },
};

export default searchApiService;


// WEBPACK FOOTER //
// ./src/main/search/services/searchApiService.js